/**
 * Loan fee override fields
 */

import { z } from 'zod';

import { CreateLoanRow } from '@willow/types-iso';

const validatePrepaymentPenaltyFeeFields = (data: CreateLoanRow, ctx: z.RefinementCtx) => {
  const {
    prepaymentPenaltyFlag,
    prepaymentPenaltyExpirationMonths,
    prepaymentPenaltyPlanDescription,
    prepaymentPenaltyPlanType,
  } = data;

  if (prepaymentPenaltyFlag != null) return;
  if (prepaymentPenaltyExpirationMonths != null) {
    ctx.addIssue({
      code: 'custom',
      path: ['prepaymentPenaltyExpirationMonths'],
      message: 'prepaymentPenaltyFlag must be defined to board loan with prepayment penalty information',
    });
  }
  if (prepaymentPenaltyPlanDescription) {
    ctx.addIssue({
      code: 'custom',
      path: ['prepaymentPenaltyPlanDescription'],
      message: 'prepaymentPenaltyFlag must be defined to board loan with prepayment penalty information',
    });
  }
  if (prepaymentPenaltyPlanType) {
    ctx.addIssue({
      code: 'custom',
      path: ['prepaymentPenaltyPlanType'],
      message: 'prepaymentPenaltyFlag must be defined to board loan with prepayment penalty information',
    });
  }
};

const validateLateFeeFields = (data: CreateLoanRow, ctx: z.RefinementCtx) => {
  const { lateFeeType, lateFeePercent, lateFeeDecimal, lateFeeMin, lateFeeMax, lateFeeFixedAmount } = data;
  if (lateFeeType === 'fixed') {
    if (lateFeeFixedAmount == null) {
      ctx.addIssue({
        code: 'custom',
        path: ['lateFeeType'],
        message:
          'A "fixed" late fee type requires the field `lateFeeFixedAmount` to be defined in order to apply the late fee overrides for this loan',
      });
    }

    const nonUsedFields = [lateFeePercent, lateFeeDecimal, lateFeeMin, lateFeeMax];
    for (const field of nonUsedFields) {
      if (field != null) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: `A "fixed" late fee type does not use the field ${field}`,
        });
      }
    }
    return;
  }

  if (lateFeeType === 'percent') {
    if (lateFeePercent == null && lateFeeDecimal == null) {
      ctx.addIssue({
        code: 'custom',
        path: ['lateFeeType'],
        message:
          'A "percent" late fee type requires the field `lateFeePercent` OR `lateFeeDecimal` to be defined in order to apply the late fee overrides for this loan',
      });
    }

    const nonUsedFields = [lateFeeFixedAmount];
    for (const field of nonUsedFields) {
      if (field != null) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: `A "percent" late fee type does not use the field ${field}`,
        });
      }
    }

    return;
  }

  if (!lateFeeType && [lateFeePercent, lateFeeDecimal, lateFeeMin, lateFeeMax, lateFeeFixedAmount].some(Boolean)) {
    ctx.addIssue({
      code: 'custom',
      path: ['lateFeeType'],
      message: '`lateFeeType` must be defined to board loan with late fee information',
    });
  }
};

export const validateTerminationFeeFields = (data: CreateLoanRow, ctx: z.RefinementCtx) => {
  const {
    terminationFeeFlag,
    terminationFeeType,
    terminationFeePercent,
    terminationFeePercentMonths,
    terminationFeeFixedAmount,
    terminationFeeFixedMonths,
    terminationFeePerProperty,
  } = data;

  const terminationFeeDataPresent = [
    terminationFeeType,
    terminationFeePercent,
    terminationFeePercentMonths,
    terminationFeeFixedAmount,
    terminationFeeFixedMonths,
    terminationFeePerProperty,
  ]
    .map((field) => field !== null && field !== undefined)
    .some(Boolean);
  if (terminationFeeFlag == null && terminationFeeDataPresent) {
    ctx.addIssue({
      code: 'custom',
      path: ['loanId'],
      message: '`terminationFeeFlag` must be set to board loan with termination fee information',
    });
    return;
  }

  if (terminationFeeFlag !== true) return;

  if (!terminationFeeType) {
    ctx.addIssue({
      code: 'custom',
      path: ['terminationFeeType'],
      message: '`terminationFeeType` must be set when terminationFeeFlag is true',
    });
    return;
  }

  if (terminationFeeType === 'percent') {
    if (terminationFeePercent == null || terminationFeePercentMonths == null) {
      ctx.addIssue({
        code: 'custom',
        path: ['terminationFeeType'],
        message: 'A "percent" termination fee requires BOTH `terminationFeePercent` and `terminationFeePercentMonths`',
      });
    }

    const nonUsedFields = {
      terminationFeeFixedAmount,
      terminationFeeFixedMonths,
      terminationFeePerProperty,
    };
    Object.entries(nonUsedFields).forEach(([name, field]) => {
      if (field != null) {
        ctx.addIssue({
          code: 'custom',
          path: [name],
          message: 'Fixed-amount termination fee fields are not valid when terminationFeeType is "percent"',
        });
      }
    });
    return;
  }

  if (terminationFeeType === 'fixed') {
    if (terminationFeeFixedAmount == null || terminationFeeFixedMonths == null || terminationFeePerProperty == null) {
      ctx.addIssue({
        code: 'custom',
        path: ['terminationFeeType'],
        message:
          'A "fixed" termination fee requires `terminationFeeFixedAmount`, `terminationFeeFixedMonths`, and `terminationFeePerProperty`',
      });
    }
    const nonUsedFields = {
      terminationFeePercent,
      terminationFeePercentMonths,
    };
    Object.entries(nonUsedFields).forEach(([name, field]) => {
      if (field != null) {
        ctx.addIssue({
          code: 'custom',
          path: [name],
          message: 'Percent-based termination fee fields are not valid when terminationFeeType is "fixed"',
        });
      }
    });
    return;
  }

  ctx.addIssue({
    code: 'custom',
    path: ['terminationFeeType'],
    message: '`terminationFeeType` must be either "percent" or "fixed"',
  });
};

// Consider making a DRY version if we need to add another fee that follows the termination fee and principal late fee pattern in the future
export const validatePrincipalLateFeeFields = (data: CreateLoanRow, ctx: z.RefinementCtx) => {
  const {
    principalLateFeeFlag,
    principalLateFeeType,
    principalLateFeePercent,
    principalLateFeePercentDays,
    principalLateFeeFixedAmount,
    principalLateFeeFixedDays,
    principalLateFeePerProperty,
  } = data;

  const principalLateDataPresent = [
    principalLateFeeType,
    principalLateFeePercent,
    principalLateFeePercentDays,
    principalLateFeeFixedAmount,
    principalLateFeeFixedDays,
    principalLateFeePerProperty,
  ]
    .map((field) => field !== null && field !== undefined)
    .some(Boolean);

  if (principalLateFeeFlag == null && principalLateDataPresent) {
    ctx.addIssue({
      code: 'custom',
      path: ['loanId'],
      message: '`principalLateFeeFlag` must be set to board loan with principal late fee information',
    });
    return;
  }

  if (principalLateFeeFlag !== true) return;

  if (!principalLateFeeType) {
    ctx.addIssue({
      code: 'custom',
      path: ['principalLateFeeType'],
      message: '`principalLateFeeType` must be set when principalLateFeeFlag is true',
    });
    return;
  }

  if (principalLateFeeType === 'percent') {
    if (principalLateFeePercent == null || principalLateFeePercentDays == null) {
      ctx.addIssue({
        code: 'custom',
        path: ['principalLateFeeType'],
        message:
          'A "percent" principal late fee requires BOTH `principalLateFeePercent` and `principalLateFeePercentDays`',
      });
    }

    const nonUsedFields = {
      principalLateFeeFixedAmount,
      principalLateFeeFixedDays,
      principalLateFeePerProperty,
    };
    Object.entries(nonUsedFields).forEach(([name, field]) => {
      if (field != null) {
        ctx.addIssue({
          code: 'custom',
          path: [name],
          message: 'Fixed-amount principal late fee fields are not valid when principalLateFeeType is "percent"',
        });
      }
    });
    return;
  }

  if (principalLateFeeType === 'fixed') {
    if (
      principalLateFeeFixedAmount == null ||
      principalLateFeeFixedDays == null ||
      principalLateFeePerProperty == null
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['principalLateFeeType'],
        message:
          'A "fixed" principal late fee requires `principalLateFeeFixedAmount`, `principalLateFeeFixedDays`, and `principalLateFeePerProperty`',
      });
    }

    const nonUsedFields = {
      principalLateFeePercent,
      principalLateFeePercentDays,
    };
    Object.entries(nonUsedFields).forEach(([name, field]) => {
      if (field != null) {
        ctx.addIssue({
          code: 'custom',
          path: [name],
          message: 'Percent-based principal late fee fields are not valid when principalLateFeeType is "fixed"',
        });
      }
    });
    return;
  }

  ctx.addIssue({
    code: 'custom',
    path: ['principalLateFeeType'],
    message: '`principalLateFeeType` must be either "percent" or "fixed"',
  });
};

export const validateFeeFields = (data: CreateLoanRow, ctx: z.RefinementCtx) => {
  validatePrepaymentPenaltyFeeFields(data, ctx);
  validateLateFeeFields(data, ctx);
  validateTerminationFeeFields(data, ctx);
  validatePrincipalLateFeeFields(data, ctx);
};
